import { HttpClient, HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { inject } from '@angular/core';
import type { ResolveFn } from '@angular/router';
import { RedirectCommand, Router } from '@angular/router';

import { SeoService } from '@rpgc-ai/services/seo/seo.service';
import { LanguageService } from '@rpgc-ai/services/translate/language.service';

import { ECaichatRoute, NOT_FOUND_REASON_CHAT } from '@rpgc-ai/shared-interfaces/enums/routes.enum';
import type { IChatWithDetails } from '@rpgc-ai/shared-interfaces/interfaces/chat.interface';
import type { ELanguage } from '@rpgc-ai/shared-interfaces/interfaces/language.interface';
import { getLocalizedPublicName } from '@rpgc-ai/shared-interfaces/utils/localized-character-name.util';

import { catchError, map, of, tap } from 'rxjs';

export const chatSeoResolver: ResolveFn<void> = (route, state) => {
  const http = inject(HttpClient);
  const router = inject(Router);
  const seoService = inject(SeoService);
  const languageService = inject(LanguageService);

  const chatId = route.params['id'];
  if (!chatId) {
    return;
  }

  const routeLanguage = languageService.getLanguageForRoute(route);
  const lang = routeLanguage.toUpperCase() as ELanguage;

  return http.get<IChatWithDetails>(`chats/${chatId}`).pipe(
    tap((chat) => {
      const { descriptions } = chat.character;
      const title = `${getLocalizedPublicName(chat.character, lang)} | CAICHAT`;
      const description = descriptions[lang] ?? descriptions['EN' as ELanguage] ?? Object.values(descriptions)[0] ?? '';

      seoService.setTitle(title);
      seoService.setTag('description', description, 'name');
      seoService.setTag('og:title', title, 'property');
      seoService.setTag('og:description', description, 'property');
    }),
    map(() => void 0),
    catchError((error: unknown) => {
      /**
       * There is no such chat: render the 404 page instead of an empty ChatComponent.
       * The resolver runs before route activation, so the chat never starts its own
       * request cascade against the dead id — the interceptor notification for this
       * single request stays the only error the user sees. `browserUrl` keeps the
       * requested chat address in the address bar.
       */
      if (error instanceof HttpErrorResponse && error.status === HttpStatusCode.NotFound) {
        const notFoundUrl = router.createUrlTree([routeLanguage, ECaichatRoute.NOT_FOUND, NOT_FOUND_REASON_CHAT]);

        return of(new RedirectCommand(notFoundUrl, { browserUrl: state.url }));
      }

      // eslint-disable-next-line no-console
      console.error('chatSeoResolver: failed to load chat SEO data', error);
      return of(void 0);
    }),
  );
};
